// SessionTracePanel
//
// Inline panel rendered on an expanded DetectionAlerts row when the analyst
// clicks the Trace Session button. The button POSTs to
// /api/alerts/{alertId}/session-trace with { tenantId, sessionId, startUtc,
// userId, hours=24 }; the backend runs three Databricks SQL passes (direct
// AAD session match, UserId-window fallback, IP enrichment) and returns
// synchronously in 5-15s.
//
// Two tabs:
//   Timeline — chronological table of every event tied to the session (or
//              user-window fallback) with operation / IP / provider / match
//              source. Helps the analyst see exactly what was done with the
//              token.
//   Summary  — distinct IPs with their providers, sensitive operations
//              surfaced (ModifyFolderPermissions, New-InboxRule, etc.), top
//              operations by frequency.
//
// Loading / error / empty states all render in-place; the panel never throws.
// All array fields are defensively coerced — PowerShell ConvertTo-Json
// unwraps single-element arrays to their scalar value, so a 1-element list
// from the API could land as a string. The toArr() helper coerces it back.

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import CopyButton from '../CopyButton';
import RawEventJsonPanel from '../sourceEvents/RawEventJsonPanel';
import { normalizeIsoUtc, resolveTz } from '../../utils/timeFormat';

const SENSITIVE_TONE = 'bg-amber-950/40 border-amber-500/30 text-amber-200';

function toArr(v) {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  return [v];
}

function fmtTs(ts, tzIana) {
  if (!ts) return '—';
  try {
    // resolveTz maps the "Local" empty iana → browser zone (an empty timeZone
    // throws); normalizeIsoUtc handles zone-less / sub-ms timestamps so the parse
    // is correct across browsers. Show the legacy Z shape ONLY for an actual UTC
    // selection — not for "Local". See feedback-local-tz-empty-iana.
    const zone = resolveTz(tzIana);
    const d = new Date(normalizeIsoUtc(ts));
    if (zone === 'UTC') {
      return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, 'Z');
    }
    return d.toLocaleString('en-US', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return String(ts); }
}

function MatchBadge({ source }) {
  if (source === 'session') {
    return <span className="px-1.5 py-0.5 rounded text-[10px] bg-sky-950/40 border border-sky-500/30 text-sky-300">session</span>;
  }
  return <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-800/60 border border-gray-700 text-gray-400">user-window</span>;
}

export default function SessionTracePanel({
  alert, sessionTrace, loading, error, onRun, tzIana,
}) {
  const [tab, setTab] = useState('timeline');
  const [expanded, setExpanded] = useState({});  // eventId -> bool
  const [collapsed, setCollapsed] = useState(false);

  const hasSession = !!alert.aadSessionId;

  const sortedEvents = useMemo(() => {
    const events = toArr(sessionTrace?.events);
    return [...events].sort((a, b) => {
      const ta = a?.time ? new Date(a.time).getTime() : 0;
      const tb = b?.time ? new Date(b.time).getTime() : 0;
      return ta - tb;
    });
  }, [sessionTrace?.events]);

  const summary           = sessionTrace?.summary || null;
  const sensitiveOps      = toArr(summary?.sensitiveOpCounts);
  const topOps            = toArr(summary?.topOperations);
  const providersTouched  = toArr(summary?.providersTouched);
  const ipSummary         = toArr(sessionTrace?.ipSummary);

  const durationSec = sessionTrace?.durationMs
    ? Math.max(1, Math.round(sessionTrace.durationMs / 1000))
    : null;

  return (
    <div className="px-6 py-3 border-b border-gray-800/60">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}
          className="flex items-center gap-1 text-gray-400 hover:text-gray-200 text-xs font-semibold tracking-wide uppercase"
        >
          {collapsed ? <ChevronRightIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
          Session Trace (from Event timestamp to +24h)
          {summary && (
            <span className="ml-2 normal-case font-normal text-gray-500">
              · {summary.eventCount} events · {summary.distinctIpCount} IPs
              {sensitiveOps.length ? ` · ${sensitiveOps.length} sensitive ops` : ''}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2">
          {!hasSession && (
            <span className="text-[11px] text-gray-500 italic" title="The alert's raw event doesn't carry a sessionId; the session-trace pivot cannot run.">
              No session ID on event
            </span>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRun(); }}
            disabled={!hasSession || loading}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-500/40 bg-blue-950/30 text-blue-300 hover:bg-blue-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <ArrowPathIcon className="w-3 h-3" />}
            {sessionTrace ? 'Re-run' : (loading ? 'Searching…' : 'Run Trace')}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {error && (
            <div className="text-[11px] px-2 py-1 rounded bg-red-950/40 border border-red-500/30 text-red-300 mb-2">
              Session trace failed: {error}
            </div>
          )}

          {!sessionTrace && !loading && !error && (
            <p className="text-[11px] text-gray-500 italic">
              {hasSession
                ? `Click Run Trace to pull every event for AAD session ${alert.aadSessionId} over the 24 hours starting at the alert event time.`
                : "Available when the alert's raw event carries an AAD sessionId (graph_signin or Exchange events)."}
            </p>
          )}

          {loading && !sessionTrace && (
            <p className="text-[11px] text-sky-400">
              Searching Databricks for session {alert.aadSessionId} (expect ~5-15s)…
            </p>
          )}

          {sessionTrace && (
            <>
              {/* Tab bar */}
              <div className="flex items-center gap-1 mb-2 border-b border-gray-800/60">
                {['timeline','summary'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTab(t); }}
                    className={`px-2 py-1 text-[11px] uppercase tracking-wide border-b ${tab === t ? 'border-blue-400 text-blue-300' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
                  >{t}</button>
                ))}
                <span className="ml-auto text-[10px] text-gray-600">
                  {sessionTrace.startUtc} → {sessionTrace.endUtc}{durationSec != null ? ` · ${durationSec}s` : ''}
                </span>
              </div>

              {tab === 'timeline' && (
                <div className="border border-gray-800 rounded overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-900/60 text-gray-400">
                      <tr>
                        <th className="w-6"></th>
                        <th className="text-left px-2 py-1 font-normal">Time</th>
                        <th className="text-left px-2 py-1 font-normal">Log type</th>
                        <th className="text-left px-2 py-1 font-normal">Operation</th>
                        <th className="text-left px-2 py-1 font-normal">Client IP</th>
                        <th className="text-left px-2 py-1 font-normal">Provider</th>
                        <th className="text-left px-2 py-1 font-normal">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedEvents.map((ev, idx) => {
                        const eid = ev.eventId || `${idx}-${ev.time}`;
                        const isOpen = !!expanded[eid];
                        const opLc = (ev.operation || '').toLowerCase();
                        const sensitive = sensitiveOps.some(s => (s?.operation || '').toLowerCase() === opLc);
                        const rowTone = sensitive ? 'bg-amber-950/20 hover:bg-amber-950/40' : (idx % 2 ? 'bg-gray-900/30 hover:bg-gray-900/60' : 'hover:bg-gray-900/50');
                        return (
                          <React.Fragment key={eid}>
                            <tr
                              className={`border-t border-gray-800/60 cursor-pointer ${rowTone}`}
                              onClick={(e) => { e.stopPropagation(); setExpanded(p => ({ ...p, [eid]: !isOpen })); }}
                            >
                              <td className="px-1">{isOpen ? <ChevronDownIcon className="w-3 h-3 text-gray-500" /> : <ChevronRightIcon className="w-3 h-3 text-gray-500" />}</td>
                              <td className="px-2 py-1 font-mono text-gray-300 whitespace-nowrap">{fmtTs(ev.time, tzIana)}</td>
                              <td className="px-2 py-1 text-gray-400">{ev.logType}</td>
                              <td className={`px-2 py-1 ${sensitive ? 'text-amber-300 font-semibold' : 'text-gray-200'}`}>{ev.operation || '—'}</td>
                              <td className="px-2 py-1 font-mono text-gray-300">{ev.clientIp || '—'}</td>
                              <td className="px-2 py-1 text-gray-400 truncate" style={{ maxWidth: 220 }}>{ev.provider || '—'}</td>
                              <td className="px-2 py-1"><MatchBadge source={ev.matchSource} /></td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-gray-950/80">
                                <td colSpan={7} className="px-4 py-2">
                                  <RawEventJsonPanel rawEventJson={ev.rawEventJson} title="Source event JSON" />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {sortedEvents.length === 0 && (
                        <tr><td colSpan={7} className="px-2 py-3 text-center text-gray-500 italic">No events found in the 24h window.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === 'summary' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Sensitive operations</p>
                    {sensitiveOps.length ? (
                      <div className="flex flex-wrap gap-1">
                        {sensitiveOps.map(s => (
                          <span key={s.operation} className={`text-[11px] px-2 py-0.5 rounded border ${SENSITIVE_TONE}`}>
                            {s.operation} · {s.count}
                          </span>
                        ))}
                      </div>
                    ) : <p className="text-[11px] text-gray-500 italic">None observed.</p>}
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Top operations</p>
                    {topOps.length ? (
                      <ul className="text-[11px] text-gray-300 space-y-0.5">
                        {topOps.slice(0, 10).map(op => (
                          <li key={op.operation} className="flex justify-between">
                            <span className="text-gray-300">{op.operation}</span>
                            <span className="font-mono text-gray-500">{op.count}</span>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-[11px] text-gray-500 italic">No operations.</p>}
                  </div>

                  <div className="md:col-span-2">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">IPs touched ({ipSummary.length})</p>
                    {ipSummary.length ? (
                      <table className="w-full text-[11px] border border-gray-800 rounded">
                        <thead className="bg-gray-900/60 text-gray-400">
                          <tr>
                            <th className="text-left px-2 py-1 font-normal">IP</th>
                            <th className="text-left px-2 py-1 font-normal">Provider</th>
                            <th className="text-left px-2 py-1 font-normal">Network</th>
                            <th className="text-left px-2 py-1 font-normal">Country</th>
                            <th className="text-left px-2 py-1 font-normal">Risk</th>
                            <th className="text-left px-2 py-1 font-normal">Service tier</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ipSummary.map(ip => (
                            <tr key={ip.ip} className="border-t border-gray-800/60">
                              <td className="px-2 py-1 font-mono text-gray-300">
                                <span className="group inline-flex items-center gap-1">
                                  {ip.ip}
                                  <CopyButton text={ip.ip} />
                                </span>
                              </td>
                              <td className="px-2 py-1 text-gray-400">{ip.provider || '—'}</td>
                              <td className="px-2 py-1 text-gray-400">{ip.networkType || '—'}</td>
                              <td className="px-2 py-1 text-gray-400">{ip.country || '—'}</td>
                              <td className="px-2 py-1 font-mono text-gray-400">{ip.riskLevel != null ? ip.riskLevel : '—'}</td>
                              <td className="px-2 py-1 text-gray-400">{ip.azureServiceTier || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : <p className="text-[11px] text-gray-500 italic">No IPs.</p>}
                  </div>

                  {providersTouched.length > 0 && (
                    <div className="md:col-span-2">
                      <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Providers touched</p>
                      <div className="flex flex-wrap gap-1">
                        {providersTouched.map(p => (
                          <span key={p} className="text-[11px] px-2 py-0.5 rounded border border-gray-700 bg-gray-900/40 text-gray-300">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
